import type { ReactNode } from "react";

import { GearIcon, LogoutIcon, UserIcon } from "./icons";

export interface ProfileCardProps {
  /** Display name (bold line). */
  name: string;
  /** Secondary line — typically the e-mail. */
  email?: string;
  /** Optional avatar image URL; falls back to a silhouette glyph. */
  avatarSrc?: string;
  /** Settings action label (e.g. "Instellingen"). */
  settingsLabel: string;
  /** Logout action label (e.g. "Uitloggen"). */
  logoutLabel: string;
  /** Settings handler — omit to hide the settings action. */
  onSettings?: () => void;
  /** Logout handler. */
  onLogout: () => void;
  /**
   * Optional handler for clicking the identity header (avatar + name/email). When set, the header
   * becomes a button — used to open the personal "Voorkeuren" screen (Jmix profile-card-click parity).
   * Omit it and the header stays a plain, non-interactive block.
   */
  onProfile?: () => void;
}

/**
 * jve-2-6: the sidebar profile card — avatar + name/email header over a split
 * Settings | Logout action row (logout tinted danger). Mirrors the Jmix `.fawkes-profile-card`.
 * Presentational + extraction-ready; the host passes the resolved identity + handlers.
 */
export function ProfileCard({
  name,
  email,
  avatarSrc,
  settingsLabel,
  logoutLabel,
  onSettings,
  onLogout,
  onProfile,
}: ProfileCardProps) {
  const headerContent: ReactNode = (
    <>
      <span
        className="fui-profile__avatar"
        data-photo={avatarSrc ? "" : undefined}
        style={avatarSrc ? { backgroundImage: `url(${avatarSrc})` } : undefined}
        aria-hidden="true"
      >
        {!avatarSrc && <UserIcon width={20} height={20} />}
      </span>
      <div className="fui-profile__meta">
        <span className="fui-profile__name">{name}</span>
        {email && (
          <span className="fui-profile__email" title={email}>
            {email}
          </span>
        )}
      </div>
    </>
  );

  return (
    <div className="fui-profile">
      {onProfile ? (
        <button
          type="button"
          className="fui-profile__header fui-profile__header--button"
          onClick={onProfile}
        >
          {headerContent}
        </button>
      ) : (
        <div className="fui-profile__header">{headerContent}</div>
      )}
      <div className="fui-profile__actions">
        {onSettings && (
          <button
            type="button"
            className="fui-profile__action fui-profile__action--settings"
            onClick={onSettings}
          >
            <GearIcon width={16} height={16} />
            <span>{settingsLabel}</span>
          </button>
        )}
        <button
          type="button"
          className="fui-profile__action fui-profile__action--logout"
          onClick={onLogout}
        >
          <LogoutIcon width={16} height={16} />
          <span>{logoutLabel}</span>
        </button>
      </div>
    </div>
  );
}
